import React, { useState, useEffect } from 'react';
import { Box, Paper, Transition, ActionIcon, Group, Title, createStyles, Tooltip, MediaQuery, useMantineTheme } from '@mantine/core';
import { IconChecklist, IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react';
import TodoList from './TodoList';
import { CourseContent, CourseState } from '../types';

interface CourseLayoutProps {
  children: React.ReactNode;
  moduleContent: CourseContent;
  courseState: CourseState;
  isOpen?: boolean;
  courseId: string;
}

// More responsive panel width
const PANEL_WIDTH_DESKTOP = '40%';
const PANEL_WIDTH_TABLET = '50%';
const PANEL_WIDTH_MOBILE = '100%';
const TRANSITION_DURATION = 300;

const useStyles = createStyles((theme, { isOpen }: { isOpen: boolean }) => ({
  toggleButton: {
    position: 'fixed',
    right: isOpen ? 
      `calc(${theme.fn.largerThan('sm') ? PANEL_WIDTH_DESKTOP : PANEL_WIDTH_MOBILE})` : 
      0,
    top: '50%',
    transform: 'translateY(-50%)',
    transition: `all ${TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    zIndex: 300,
    boxShadow: isOpen ? 'none' : theme.shadows.md,
    border: `1px solid ${isOpen ? theme.colors.blue[3] : theme.colors.gray[3]}`,
    backgroundColor: isOpen ? theme.colors.blue[0] : theme.white,
    width: 42,
    height: 42,
    '&:hover': {
      backgroundColor: isOpen ? theme.colors.blue[1] : theme.colors.gray[0],
      transform: isOpen 
        ? 'translateY(-50%) translateX(2px)'
        : 'translateY(-50%) translateX(-2px)',
    },
    '&:active': {
      transform: 'translateY(-50%)',
    },
    borderTopRightRadius: isOpen ? 0 : theme.radius.md,
    borderBottomRightRadius: isOpen ? 0 : theme.radius.md,
    borderTopLeftRadius: isOpen ? theme.radius.md : 0,
    borderBottomLeftRadius: isOpen ? theme.radius.md : 0,
    borderRight: isOpen ? 'none' : undefined,
    borderLeft: !isOpen ? 'none' : undefined,
    [theme.fn.smallerThan('sm')]: {
      // Different positioning for mobile
      right: isOpen ? '10px' : 0,
      top: isOpen ? '70px' : '50%',
      transform: isOpen ? 'none' : 'translateY(-50%)',
      '&:hover': {
        transform: isOpen ? 'none' : 'translateY(-50%) translateX(-2px)',
      },
    },
  },
  toggleIcon: {
    color: isOpen ? theme.colors.blue[6] : theme.colors.gray[6],
    transition: 'color 300ms ease',
  },
  panel: {
    position: 'fixed',
    top: 60,
    right: 0,
    width: PANEL_WIDTH_DESKTOP,
    height: 'calc(100vh - 60px)',
    borderLeft: `1px solid ${theme.colors.gray[3]}`,
    zIndex: 299,
    backgroundColor: theme.white,
    padding: theme.spacing.md,
    boxShadow: theme.shadows.md,
    display: 'flex',
    flexDirection: 'column',
    [theme.fn.smallerThan('md')]: {
      width: PANEL_WIDTH_TABLET,
    },
    [theme.fn.smallerThan('sm')]: {
      width: PANEL_WIDTH_MOBILE,
      top: 60,
      height: 'calc(100vh - 60px)',
      paddingBottom: theme.spacing.xl, // Extra padding at bottom for mobile
    },
  },
  panelContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    position: 'relative',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.colors.gray[3],
      borderRadius: '3px',
    },
  },
  mainContent: {
    flex: 1,
    marginRight: isOpen ? 
      theme.fn.largerThan('sm') ? PANEL_WIDTH_DESKTOP : 0 : 
      0,
    transition: `margin-right ${TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    marginTop: 60,
    maxWidth: isOpen ? 
      theme.fn.largerThan('sm') ? `calc(100% - ${PANEL_WIDTH_DESKTOP})` : '100%' : 
      '100%',
    position: 'relative',
    zIndex: 1,
    [theme.fn.smallerThan('md')]: {
      marginRight: isOpen ? PANEL_WIDTH_TABLET : 0,
      maxWidth: isOpen ? `calc(100% - ${PANEL_WIDTH_TABLET})` : '100%',
    },
    [theme.fn.smallerThan('sm')]: {
      marginRight: 0,
      maxWidth: '100%',
      display: isOpen ? 'none' : 'block', // Hide content when panel is open on mobile
    },
  },
  container: {
    display: 'flex',
    position: 'relative',
    minHeight: 'calc(100vh - 60px)',
    overflow: 'hidden',
  },
  mobileCloseButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 301,
    display: 'none',
    [theme.fn.smallerThan('sm')]: {
      display: 'block',
    },
  }
}));

const CourseLayout: React.FC<CourseLayoutProps> = ({ 
  children, 
  moduleContent, 
  courseState,
  isOpen = false,
  courseId
}) => {
  const [todosPanelOpen, setTodosPanelOpen] = useState(isOpen);
  const theme = useMantineTheme();
  const { classes } = useStyles({ isOpen: todosPanelOpen });
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (todosPanelOpen !== isOpen && !isTransitioning) {
      setIsTransitioning(true);
      setTodosPanelOpen(isOpen);
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const togglePanel = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setTodosPanelOpen(prev => !prev);
      setTimeout(() => {
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
    }
  };

  return (
    <Box className={classes.container}>
      {/* Main content */}
      <Box className={classes.mainContent}>
        {children}
      </Box>

      {/* Todos Panel Toggle Button */}
      <Tooltip 
        label={todosPanelOpen ? "Hide Progress Tracker" : "Show Progress Tracker"}
        position={todosPanelOpen ? "left" : "right"}
      >
        <ActionIcon
          variant="subtle"
          size="xl"
          className={classes.toggleButton}
          onClick={togglePanel}
          disabled={isTransitioning}
        >
          {todosPanelOpen ? (
            <MediaQuery largerThan="sm" styles={{ display: 'block' }}>
              <IconChevronRight size={24} className={classes.toggleIcon} />
            </MediaQuery>
          ) : (
            <IconChevronLeft size={24} className={classes.toggleIcon} />
          )}
        </ActionIcon>
      </Tooltip>

      {/* Todos Panel */}
      <Transition 
        mounted={todosPanelOpen} 
        transition={{
          in: { transform: 'translateX(0)', opacity: 1 },
          out: { transform: 'translateX(100%)', opacity: 0 },
          common: { transition: `transform ${TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)` },
          transitionProperty: 'transform, opacity',
        }}
        duration={TRANSITION_DURATION}
        keepMounted
      >
        {(transitionStyles) => (
          <Paper
            className={classes.panel}
            style={transitionStyles}
            shadow="md"
          >
            {/* Mobile close button */}
            <MediaQuery largerThan="sm" styles={{ display: 'none' }}>
              <ActionIcon 
                className={classes.mobileCloseButton}
                variant="filled"
                color="blue"
                onClick={togglePanel}
              >
                <IconX size={16} />
              </ActionIcon>
            </MediaQuery>
            
            <Group position="apart" mb="md">
              <Title order={3} size="h4">Progress Tracker</Title>
              <IconChecklist size={24} />
            </Group>
            <Box className={classes.panelContent}>
              <TodoList
                moduleContent={moduleContent}
                courseState={courseState}
                courseId={courseId}
              />
            </Box>
          </Paper>
        )}
      </Transition>
    </Box>
  );
};

export default CourseLayout; 